import { useCallback, useEffect, useRef, useState } from 'react';
import { ASSISTANT_NAME } from '../lib/assistant';
import { api, apiStream, type ApiError } from '../api/client';
import { VoiceButton } from './VoiceButton';
import {
  setSpokenReplies,
  speak,
  speechOutputSupported,
  spokenRepliesOn,
  stopSpeaking,
} from '../lib/speech';

/**
 * The course assistant, inside the player it belongs to.
 *
 * Deliberately a third tab in the rail that already holds Curriculum and
 * Notes, rather than a new surface. A learner asking about the lesson they
 * are on should not have to leave it, and the player did not need
 * redesigning to accommodate one more panel.
 *
 * Three things the interface has to get right, because they are the whole
 * argument for trusting the answer:
 *
 *   * A refusal looks like a refusal. "I couldn't find that" is rendered as a
 *     quiet, sourceless note, not as an answer with the confidence of one.
 *   * Sources are shown, and they say where: the module, the lesson, and a
 *     timecode or page. A citation a learner cannot act on is decoration.
 *   * Waiting is honest. On a CPU host an answer takes tens of seconds, so
 *     the panel says so rather than showing a spinner that implies promptness.
 */

interface Source {
  rank: number;
  label: string;
  lessonId: string | null;
  lessonTitle: string | null;
  moduleTitle: string | null;
  sourceKind: string;
  pageNumber: number | null;
  startSeconds: number | null;
  endSeconds: number | null;
  timecode: string | null;
  score: number;
}

interface AskResponse {
  requestId: string;
  conversationUuid: string;
  messageUuid: string | null;
  answer: string;
  refused: boolean;
  grounded: boolean;
  sources: Source[];
}

interface Turn {
  id: string;
  question: string;
  answer: string;
  refused: boolean;
  sources: Source[];
  messageUuid: string | null;
  requestId: string;
  feedback?: 'up' | 'down';
  /** True while the answer is still being written. */
  streaming?: boolean;
}

interface Props {
  courseId: string;
  /** The lesson the learner is on. Ranks passages; never restricts them. */
  lessonId?: string;
  /** Jump the player to a cited lesson, and to a moment inside it. */
  onOpenSource?: (lessonId: string, startSeconds: number | null) => void;
}

/** Long enough that a learner stops wondering whether it is broken. */
const SLOW_AFTER_MS = 8_000;

export function AskSkillXzenAi({ courseId, lessonId, onOpenSource }: Props) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [slow, setSlow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [openSources, setOpenSources] = useState<Record<string, boolean>>({});
  const [speakReplies, setSpeakReplies] = useState(spokenRepliesOn);

  const endRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Read inside `ask` rather than closed over, so toggling the voice does not
  // rebuild the callback and so a toggle mid-answer takes effect on that
  // answer.
  const speakRef = useRef(speakReplies);
  speakRef.current = speakReplies;

  // Leaving the panel, or the page, should stop it talking.
  useEffect(() => () => stopSpeaking(), []);

  // Suggestions come from the course's own structure, so every one of them is
  // answerable. A suggestion the material cannot answer teaches a learner
  // that the assistant does not work.
  useEffect(() => {
    let cancelled = false;
    const query = lessonId ? `?lessonId=${encodeURIComponent(lessonId)}` : '';
    api<{ suggestions: string[] }>(`/ai/courses/${courseId}/suggestions${query}`)
      .then((payload) => {
        if (!cancelled) setSuggestions(payload.suggestions);
      })
      .catch((caught: ApiError) => {
        if (cancelled) return;
        // 503 means the feature is off or unconfigured. That is not an error
        // the learner caused, so the panel explains itself and offers nothing.
        if (caught.status === 503) setUnavailable(caught.message);
      });
    return () => {
      cancelled = true;
    };
  }, [courseId, lessonId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns.length, asking]);

  const ask = useCallback(
    async (text: string, spokenQuestion = false) => {
      const trimmed = text.trim();
      if (trimmed.length < 3 || asking) return;

      setAsking(true);
      setSlow(false);
      setError(null);
      setQuestion('');
      const slowTimer = setTimeout(() => setSlow(true), SLOW_AFTER_MS);

      // Abandoning a question should stop the model, not just stop listening.
      const controller = new AbortController();
      abortRef.current = controller;

      /** The turn being written, by the id the server gave it. */
      let turnId: string | null = null;

      const upsert = (id: string, patch: Partial<Turn>): void => {
        setTurns((previous) => {
          const index = previous.findIndex((entry) => entry.id === id);
          if (index === -1) {
            return [
              ...previous,
              {
                id,
                question: trimmed,
                answer: '',
                refused: false,
                sources: [],
                messageUuid: null,
                requestId: id,
                streaming: true,
                ...patch,
              },
            ];
          }
          const next = [...previous];
          next[index] = { ...next[index]!, ...patch };
          return next;
        });
      };

      try {
        for await (const frame of apiStream(
          `/ai/courses/${courseId}/ask/stream`,
          {
            question: trimmed,
            lessonId,
            // Carried so the assistant can resolve "it" from the previous
            // answer. The server bounds how much history reaches the model.
            conversationId: conversationId ?? undefined,
          },
          controller.signal,
        )) {
          if (frame.event === 'meta') {
            const meta = frame.data as { requestId: string; conversationUuid: string };
            turnId = meta.requestId;
            setConversationId(meta.conversationUuid || null);
            upsert(meta.requestId, {});
            // The first token can be half a minute away; the question
            // appearing immediately is what tells a learner it was heard.
            setSlow(false);
          } else if (frame.event === 'delta' && turnId) {
            const { text: delta } = frame.data as { text: string };
            setTurns((previous) =>
              previous.map((entry) =>
                entry.id === turnId ? { ...entry, answer: entry.answer + delta } : entry,
              ),
            );
          } else if (frame.event === 'replace' && turnId) {
            // The guard stopped the stream. What was shown is replaced rather
            // than appended to, because part of it is being withdrawn.
            const { text: replacement } = frame.data as { text: string };
            upsert(turnId, { answer: replacement });
          } else if (frame.event === 'done') {
            const result = frame.data as { result: AskResponse };
            const done = result.result;
            setConversationId(done.conversationUuid || null);
            upsert(turnId ?? done.requestId, {
              answer: done.answer,
              refused: done.refused,
              sources: done.sources,
              messageUuid: done.messageUuid,
              requestId: done.requestId,
              streaming: false,
            });
            // Spoken only once the answer is final. Reading the stream aloud
            // as it arrives would mean speaking text the guard may still
            // withdraw, and a voice cannot take words back. A question asked
            // aloud is answered aloud whatever the switch says: the learner
            // who spoke is the one most likely not to be reading the screen.
            if ((speakRef.current || spokenQuestion) && !done.refused) speak(done.answer);
          } else if (frame.event === 'error') {
            const { message } = frame.data as { message: string };
            setError(message);
            if (turnId) upsert(turnId, { streaming: false });
          }
        }
      } catch (caught) {
        if (controller.signal.aborted) {
          // The learner stopped it. Not an error, and the partial answer
          // stays on screen rather than vanishing.
          if (turnId) upsert(turnId, { streaming: false });
        } else {
          const apiError = caught as ApiError;
          if (apiError.status === 503) setUnavailable(apiError.message);
          else if (apiError.status === 429)
            setError('That is a lot of questions in one go. Please wait a little and try again.');
          else setError(apiError.message ?? 'The answer could not be completed.');
          if (turnId) upsert(turnId, { streaming: false });
        }
      } finally {
        clearTimeout(slowTimer);
        abortRef.current = null;
        setAsking(false);
        setSlow(false);
      }
    },
    [asking, conversationId, courseId, lessonId],
  );

  const stop = useCallback(() => abortRef.current?.abort(), []);

  const sendFeedback = useCallback(async (turn: Turn, helpful: boolean) => {
    if (!turn.messageUuid) return;
    // Optimistic: the thumb is a courtesy, and a failed one is not worth
    // interrupting a learner over.
    setTurns((previous) =>
      previous.map((entry) =>
        entry.id === turn.id ? { ...entry, feedback: helpful ? 'up' : 'down' } : entry,
      ),
    );
    try {
      await api(`/ai/messages/${turn.messageUuid}/feedback`, {
        method: 'POST',
        body: JSON.stringify({ helpful }),
      });
    } catch {
      /* left as marked; the next page load will show the truth */
    }
  }, []);

  const clear = useCallback(() => {
    setTurns([]);
    setConversationId(null);
    setError(null);
  }, []);

  if (unavailable) {
    return (
      <div className="aipanel">
        <div className="state">
          <div className="state__art" aria-hidden="true">✨</div>
          <h4>{ASSISTANT_NAME} is not available</h4>
          <p className="muted">{unavailable}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="aipanel">
      <div className="aipanel__intro">
        <p className="aipanel__sub">
          Ask about this course. Answers come from the course material only — if it is not
          in there, the assistant will say so.
        </p>
      </div>

      <div className="aipanel__thread" role="log" aria-live="polite">
        {turns.length === 0 && suggestions.length > 0 ? (
          <div className="aipanel__suggest">
            <p className="aipanel__suggesthead">Suggested questions</p>
            <ul>
              {suggestions.map((suggestion) => (
                <li key={suggestion}>
                  <button className="aipanel__chip" onClick={() => void ask(suggestion)}>
                    {suggestion}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {turns.map((turn) => (
          <article key={turn.id} className="aiturn">
            <p className="aiturn__q">{turn.question}</p>

            <div className={`aiturn__a${turn.refused ? ' aiturn__a--refused' : ''}`}>
              {turn.answer.split('\n\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>

            {turn.sources.length > 0 ? (
              <div className="aiturn__sources">
                <button
                  className="aiturn__srctoggle"
                  aria-expanded={openSources[turn.id] ?? true}
                  onClick={() =>
                    setOpenSources((previous) => ({
                      ...previous,
                      [turn.id]: !(previous[turn.id] ?? true),
                    }))
                  }
                >
                  {turn.sources.length} source{turn.sources.length === 1 ? '' : 's'}
                </button>

                {(openSources[turn.id] ?? true) ? (
                  <ul>
                    {turn.sources.map((source) => (
                      <li key={source.rank}>
                        <button
                          className="aisource"
                          disabled={!source.lessonId || !onOpenSource}
                          onClick={() =>
                            source.lessonId &&
                            onOpenSource?.(source.lessonId, source.startSeconds)
                          }
                          title={
                            source.lessonId
                              ? 'Open this lesson at that point'
                              : 'This source is the course overview'
                          }
                        >
                          <span className="aisource__n">{source.rank}</span>
                          <span className="aisource__where">
                            {source.moduleTitle ? (
                              <span className="aisource__mod">{source.moduleTitle}</span>
                            ) : null}
                            <span className="aisource__lesson">
                              {source.lessonTitle ?? source.label}
                            </span>
                          </span>
                          <span className="aisource__at">
                            {source.timecode
                              ? `🎥 ${source.timecode}`
                              : source.pageNumber !== null
                                ? `📄 p.${source.pageNumber}`
                                : ''}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}

            {turn.streaming ? null : (
            <div className="aiturn__acts">
              <button
                className="iconbtn"
                aria-label="Copy answer"
                onClick={() => void navigator.clipboard?.writeText(turn.answer)}
              >
                ⧉
              </button>
              <button
                className={`iconbtn${turn.feedback === 'up' ? ' iconbtn--on' : ''}`}
                aria-label="This answer helped"
                aria-pressed={turn.feedback === 'up'}
                onClick={() => void sendFeedback(turn, true)}
              >
                👍
              </button>
              <button
                className={`iconbtn${turn.feedback === 'down' ? ' iconbtn--on' : ''}`}
                aria-label="This answer did not help"
                aria-pressed={turn.feedback === 'down'}
                onClick={() => void sendFeedback(turn, false)}
              >
                👎
              </button>
              {/* Quoted in a support request; the server logs the same id. */}
              <span className="aiturn__id" title="Reference for support">
                {turn.requestId}
              </span>
            </div>
            )}
          </article>
        ))}

        {asking && !turns.some((turn) => turn.streaming) ? (
          <div className="aiturn aiturn--pending">
            <p className="muted">
              {slow
                ? 'Still reading the course material. On this server an answer can take a minute.'
                : 'Reading the course material…'}
            </p>
          </div>
        ) : null}

        {error ? <div className="alert alert--error">{error}</div> : null}
        <div ref={endRef} />
      </div>

      <form
        className="aipanel__ask"
        onSubmit={(event) => {
          event.preventDefault();
          void ask(question);
        }}
      >
        <textarea
          className="field__input"
          rows={2}
          value={question}
          maxLength={1000}
          placeholder="Ask a question about this course…"
          disabled={asking}
          onChange={(event) => setQuestion(event.target.value)}
          onKeyDown={(event) => {
            // Enter sends, shift+enter makes a new line — what a chat does.
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void ask(question);
            }
          }}
        />
        <div className="aipanel__askacts">
          <VoiceButton
            disabled={asking}
            // Spoken questions are asked straight away rather than dropped in
            // the box for approval. Pressing the microphone a second time is
            // already the learner saying "that's my question"; making them
            // then press Ask is a step that exists only because the text
            // happened to arrive by voice.
            onTranscript={(text) => {
              setQuestion(text);
              void ask(text, true);
            }}
          />
          {speechOutputSupported() ? (
            <button
              type="button"
              className={`btn btn--quiet btn--sm${speakReplies ? ' is-on' : ''}`}
              aria-pressed={speakReplies}
              onClick={() => {
                const next = !speakReplies;
                setSpeakReplies(next);
                setSpokenReplies(next);
                if (!next) stopSpeaking();
              }}
              title={speakReplies ? 'Stop reading answers aloud' : 'Read answers aloud'}
            >
              {/* Named for what it does. "Voice off" read as a status, and
                  learners could not tell it was the switch for spoken answers. */}
              <span aria-hidden="true">{speakReplies ? '🔊' : '🔈'}</span>{' '}
              {speakReplies ? 'Read aloud: on' : 'Read aloud: off'}
            </button>
          ) : null}
          {asking ? (
            // Stopping ends the generation rather than merely hiding it: the
            // model is doing real work on a shared machine.
            <button className="btn btn--sm btn--outline" type="button" onClick={stop}>
              Stop
            </button>
          ) : (
            <button className="btn btn--sm" type="submit" disabled={question.trim().length < 3}>
              Ask
            </button>
          )}
          {turns.length > 0 && !asking ? (
            <button className="btn btn--quiet btn--sm" type="button" onClick={clear}>
              Clear
            </button>
          ) : null}
        </div>
      </form>
    </div>
  );
}
